import { useEffect, useMemo, useRef, useState } from "react";
import { assetUrl } from "../../api/http";

type Props = {
  tshirtCustomization?: any;
  fallbackColor?: string;
  title?: string;
  className?: string;
};

type TshirtSide = "front" | "back";

type TshirtLayout = {
  side: TshirtSide;
  shirtX: number;
  shirtY: number;
  shirtW: number;
  shirtH: number;
  bodyX: number;
  bodyY: number;
  bodyW: number;
  bodyH: number;
  neckW: number;
  neckDepth: number;
  sleeveY: number;
  printX: number;
  printY: number;
  printW: number;
  printH: number;
  textX: number;
  textY: number;
  textW: number;
  textH: number;
};

type TextMetrics = {
  text: string;
  centerX: number;
  centerY: number;
  width: number;
  height: number;
  left: number;
  top: number;
  right: number;
  bottom: number;
};

type TemplateContentBounds = {
  xPct: number;
  yPct: number;
  widthPct: number;
  heightPct: number;
};

type SideRenderData = {
  imageUrl: string;
  scale: number;
  rotation: number;
  offsetXPct: number;
  offsetYPct: number;
  text: string;
  textFont: string;
  textSizePx: number;
  textOffsetXPct: number;
  textOffsetYPct: number;
  textColor: string;
  textWeight: number;
  textItalic: boolean;
  textStroke: boolean;
  textUppercase: boolean;
  textLetterSpacingPx: number;
};

const clamp = (n: number, min: number, max: number) =>
  Math.min(max, Math.max(min, n));
const TEXT_RENDER_BASE_PRINT_HEIGHT = 280;

const resolveAsset = (value: unknown) => {
  const raw = String(value ?? "").trim();
  if (!raw) return "";
  return assetUrl(raw) || raw;
};

const toNum = (value: unknown, fallback: number) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : fallback;
};

const pickFirst = (...values: unknown[]) => {
  for (const value of values) {
    if (value !== undefined && value !== null) return value;
  }
  return undefined;
};

const toText = (value: unknown) => String(value ?? "");

function ensureCanvasSize(canvas: HTMLCanvasElement, w: number, h: number) {
  const dpr = Math.min(window.devicePixelRatio || 1, 2);
  const pxW = Math.max(1, Math.round(w * dpr));
  const pxH = Math.max(1, Math.round(h * dpr));
  if (canvas.width !== pxW || canvas.height !== pxH) {
    canvas.width = pxW;
    canvas.height = pxH;
  }
  const ctx = canvas.getContext("2d");
  if (!ctx) return null;
  ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
  return ctx;
}

function detectTemplateContentBounds(
  image: HTMLImageElement,
): TemplateContentBounds | null {
  const srcW = image.naturalWidth || image.width;
  const srcH = image.naturalHeight || image.height;
  if (!srcW || !srcH) return null;

  const maxSample = 700;
  const ratio = Math.min(1, maxSample / Math.max(srcW, srcH));
  const sampleW = Math.max(32, Math.round(srcW * ratio));
  const sampleH = Math.max(32, Math.round(srcH * ratio));
  const canvas = document.createElement("canvas");
  canvas.width = sampleW;
  canvas.height = sampleH;
  const ctx = canvas.getContext("2d", { willReadFrequently: true });
  if (!ctx) return null;
  ctx.clearRect(0, 0, sampleW, sampleH);
  ctx.drawImage(image, 0, 0, sampleW, sampleH);

  let data: Uint8ClampedArray;
  try {
    data = ctx.getImageData(0, 0, sampleW, sampleH).data;
  } catch {
    return null;
  }

  let minX = sampleW;
  let minY = sampleH;
  let maxX = -1;
  let maxY = -1;
  const alphaThreshold = 8;
  for (let y = 0; y < sampleH; y += 1) {
    const rowBase = y * sampleW * 4;
    for (let x = 0; x < sampleW; x += 1) {
      if (data[rowBase + x * 4 + 3] <= alphaThreshold) continue;
      if (x < minX) minX = x;
      if (x > maxX) maxX = x;
      if (y < minY) minY = y;
      if (y > maxY) maxY = y;
    }
  }

  if (maxX < minX || maxY < minY) return null;

  const padX = Math.max(1, Math.round((maxX - minX + 1) * 0.01));
  const padY = Math.max(1, Math.round((maxY - minY + 1) * 0.01));
  const x0 = clamp(minX - padX, 0, sampleW - 1);
  const y0 = clamp(minY - padY, 0, sampleH - 1);
  const x1 = clamp(maxX + padX, 0, sampleW - 1);
  const y1 = clamp(maxY + padY, 0, sampleH - 1);

  return {
    xPct: x0 / sampleW,
    yPct: y0 / sampleH,
    widthPct: (x1 - x0 + 1) / sampleW,
    heightPct: (y1 - y0 + 1) / sampleH,
  };
}

function getLayout(w: number, h: number, side: TshirtSide): TshirtLayout {
  const shirtH = h * 0.93;
  const shirtW = shirtH * 0.66;
  const shirtX = (w - shirtW) / 2;
  const shirtY = (h - shirtH) / 2 + h * 0.012;
  const bodyX = shirtX + shirtW * 0.205;
  const bodyY = shirtY + shirtH * 0.125;
  const bodyW = shirtW * 0.59;
  const bodyH = shirtH * 0.84;
  const neckW = bodyW * 0.44;
  const neckDepth = side === "front" ? shirtH * 0.1 : 0;
  const sleeveY = shirtY + shirtH * 0.23;
  const printInsetX = bodyW * (side === "front" ? 0.175 : 0.154);
  const printInsetTop = bodyH * (side === "front" ? 0.2625 : 0.13225);
  const printInsetBottom = bodyH * 0.08;
  const sideTrim = bodyW * 0.06;
  const basePrintX = bodyX + printInsetX;
  const printY = bodyY + printInsetTop;
  const basePrintW = Math.max(1, bodyW - printInsetX * 2);
  const basePrintH = Math.max(1, bodyH - printInsetTop - printInsetBottom);
  const printX = basePrintX + sideTrim;
  const printW = Math.max(1, basePrintW - sideTrim * 2);
  const bottomTrim = basePrintH * (side === "back" ? 0.6 : 0.5);
  const printH = Math.max(1, basePrintH - bottomTrim);
  const textX = printX;
  const textY = printY;
  const textW = printW;
  const textH = basePrintH;

  return {
    side,
    shirtX,
    shirtY,
    shirtW,
    shirtH,
    bodyX,
    bodyY,
    bodyW,
    bodyH,
    neckW,
    neckDepth,
    sleeveY,
    printX,
    printY,
    printW,
    printH,
    textX,
    textY,
    textW,
    textH,
  };
}

function getTemplateLayout(
  w: number,
  h: number,
  side: TshirtSide,
  templateBounds?: TemplateContentBounds | null,
): TshirtLayout {
  const templateAspect = 0.78;
  const maxW = w * 0.92;
  const maxH = h * 0.96;
  let shirtW = maxW;
  let shirtH = shirtW / templateAspect;
  if (shirtH > maxH) {
    shirtH = maxH;
    shirtW = shirtH * templateAspect;
  }
  const shirtX = (w - shirtW) / 2;
  const shirtY = (h - shirtH) / 2;
  const bounds = templateBounds || {
    xPct: 0,
    yPct: 0,
    widthPct: 1,
    heightPct: 1,
  };
  const boundedX = clamp(bounds.xPct, 0, 0.95);
  const boundedY = clamp(bounds.yPct, 0, 0.95);
  const boundedW = clamp(bounds.widthPct, 0.05, 1 - boundedX);
  const boundedH = clamp(bounds.heightPct, 0.05, 1 - boundedY);
  const contentX = shirtX + boundedX * shirtW;
  const contentY = shirtY + boundedY * shirtH;
  const contentW = Math.max(1, boundedW * shirtW);
  const contentH = Math.max(1, boundedH * shirtH);
  const printInsetX = Math.max(
    contentW * (side === "front" ? 0.237 : 0.215),
    shirtW * 0.105,
  );
  const printInsetTop = Math.max(
    contentH * (side === "front" ? 0.21875 : 0.13225),
    shirtH * (side === "front" ? 0.14375 : 0.092),
  );
  const printInsetBottom = Math.max(contentH * 0.08, shirtH * 0.06);
  const sideTrim = contentW * 0.06;
  const basePrintW = Math.max(1, contentW - printInsetX * 2);
  const basePrintH = Math.max(1, contentH - printInsetTop - printInsetBottom);
  const printW = Math.max(1, basePrintW - sideTrim * 2);
  const bottomTrim = basePrintH * (side === "back" ? 0.6 : 0.5);
  const printH = Math.max(1, basePrintH - bottomTrim);
  const printX = contentX + printInsetX + sideTrim;
  const printY = contentY + printInsetTop;
  const textX = printX;
  const textY = printY;
  const textW = printW;
  const textH = basePrintH;

  return {
    side,
    shirtX,
    shirtY,
    shirtW,
    shirtH,
    bodyX: contentX,
    bodyY: contentY,
    bodyW: contentW,
    bodyH: contentH,
    neckW: contentW * 0.32,
    neckDepth: side === "front" ? shirtH * 0.08 : 0,
    sleeveY: contentY + contentH * 0.22,
    printX,
    printY,
    printW,
    printH,
    textX,
    textY,
    textW,
    textH,
  };
}

function drawShirtPath(ctx: CanvasRenderingContext2D, layout: TshirtLayout) {
  const {
    shirtX,
    shirtW,
    shirtY,
    shirtH,
    bodyX,
    bodyY,
    bodyW,
    bodyH,
    sleeveY,
  } = layout;
  const shoulderY = shirtY + shirtH * 0.142;
  const leftShoulderX = shirtX + shirtW * 0.34;
  const rightShoulderX = shirtX + shirtW * 0.66;
  const leftSleeveTopOuterX = shirtX + shirtW * 0.07;
  const rightSleeveTopOuterX = shirtX + shirtW * 0.93;
  const leftSleeveBottomOuterX = shirtX + shirtW * 0.095;
  const rightSleeveBottomOuterX = shirtX + shirtW * 0.905;
  const sleeveBottomY = sleeveY + shirtH * 0.175;
  const leftArmpitX = bodyX + bodyW * 0.005;
  const rightArmpitX = bodyX + bodyW * 0.995;
  const armpitY = bodyY + bodyH * 0.16;
  const sideBottomY = bodyY + bodyH * 0.975;
  const hemY = bodyY + bodyH;

  ctx.beginPath();
  ctx.moveTo(leftShoulderX, shoulderY);
  ctx.bezierCurveTo(
    shirtX + shirtW * 0.26,
    shirtY + shirtH * 0.11,
    shirtX + shirtW * 0.15,
    shirtY + shirtH * 0.16,
    leftSleeveTopOuterX,
    sleeveY,
  );
  ctx.lineTo(leftSleeveBottomOuterX, sleeveBottomY);
  ctx.quadraticCurveTo(
    shirtX + shirtW * 0.14,
    shirtY + shirtH * 0.43,
    leftArmpitX,
    armpitY,
  );
  ctx.lineTo(bodyX + bodyW * 0.045, sideBottomY);
  ctx.quadraticCurveTo(
    shirtX + shirtW * 0.5,
    hemY + shirtH * 0.012,
    bodyX + bodyW * 0.955,
    sideBottomY,
  );
  ctx.lineTo(rightArmpitX, armpitY);
  ctx.quadraticCurveTo(
    shirtX + shirtW * 0.86,
    shirtY + shirtH * 0.43,
    rightSleeveBottomOuterX,
    sleeveBottomY,
  );
  ctx.lineTo(rightSleeveTopOuterX, sleeveY);
  ctx.bezierCurveTo(
    shirtX + shirtW * 0.85,
    shirtY + shirtH * 0.16,
    shirtX + shirtW * 0.74,
    shirtY + shirtH * 0.11,
    rightShoulderX,
    shoulderY,
  );
  ctx.closePath();
}

function drawNeckCutPath(ctx: CanvasRenderingContext2D, layout: TshirtLayout) {
  if (layout.neckDepth <= 0 || layout.side !== "front") return;
  const neckCenterX = layout.shirtX + layout.shirtW / 2;
  const shoulderY = layout.shirtY + layout.shirtH * 0.145;
  const neckTopY = shoulderY + layout.shirtH * 0.004;
  const leftX = neckCenterX - layout.neckW / 2;
  const rightX = neckCenterX + layout.neckW / 2;
  ctx.beginPath();
  ctx.moveTo(leftX, neckTopY);
  ctx.bezierCurveTo(
    leftX + layout.neckW * 0.2,
    neckTopY + layout.neckDepth * 0.92,
    rightX - layout.neckW * 0.2,
    neckTopY + layout.neckDepth * 0.92,
    rightX,
    neckTopY,
  );
  ctx.lineTo(leftX, neckTopY);
  ctx.closePath();
}

function drawBackNeckSeamPath(
  ctx: CanvasRenderingContext2D,
  layout: TshirtLayout,
) {
  if (layout.side !== "back") return;
  const neckCenterX = layout.shirtX + layout.shirtW / 2;
  const shoulderY = layout.shirtY + layout.shirtH * 0.145;
  const neckTopY = shoulderY + layout.shirtH * 0.004;
  const seamWidth = layout.neckW * 0.86;
  const seamDepth = layout.shirtH * 0.03;
  const leftX = neckCenterX - seamWidth / 2;
  const rightX = neckCenterX + seamWidth / 2;
  ctx.beginPath();
  ctx.moveTo(leftX, neckTopY);
  ctx.quadraticCurveTo(neckCenterX, neckTopY + seamDepth, rightX, neckTopY);
}

function getTextBounds(layout: TshirtLayout) {
  return { x: layout.textX, y: layout.textY, w: layout.textW, h: layout.textH };
}

function getStyledText(data: SideRenderData) {
  return data.textUppercase
    ? String(data.text || "").toUpperCase()
    : String(data.text || "");
}

function getRenderedTextSizePx(layout: TshirtLayout, data: SideRenderData) {
  const textBounds = getTextBounds(layout);
  const authored = clamp(Number(data.textSizePx ?? 24), 12, 32);
  const scaled = authored * (textBounds.h / TEXT_RENDER_BASE_PRINT_HEIGHT);
  return clamp(scaled, 8, textBounds.h * 0.72);
}

function getRenderedLetterSpacingPx(
  layout: TshirtLayout,
  data: SideRenderData,
) {
  const textBounds = getTextBounds(layout);
  const authored = Number(data.textLetterSpacingPx ?? 4);
  return authored * (textBounds.h / TEXT_RENDER_BASE_PRINT_HEIGHT);
}

function computeTextMetrics(
  ctx: CanvasRenderingContext2D,
  layout: TshirtLayout,
  data: SideRenderData,
): TextMetrics | null {
  const text = getStyledText(data).trim();
  if (!text) return null;
  const textBounds = getTextBounds(layout);
  const textSize = getRenderedTextSizePx(layout, data);
  const weight = clamp(Number(data.textWeight ?? 400), 100, 700);
  const italic = data.textItalic ? "italic " : "";
  const family = data.textFont || "Impact";
  const letterSpacing = getRenderedLetterSpacingPx(layout, data);
  ctx.font = `${italic}${weight} ${Math.round(textSize)}px "${family}"`;
  const measure = ctx.measureText(text);
  const width = Math.max(
    1,
    measure.width + Math.max(0, text.length - 1) * letterSpacing,
  );
  const height = textSize * 1.04;
  const centerX =
    textBounds.x +
    textBounds.w / 2 +
    (data.textOffsetXPct / 100) * textBounds.w * 0.9;
  const centerY =
    textBounds.y +
    textBounds.h / 2 +
    (data.textOffsetYPct / 100) * textBounds.h * 0.9;
  return {
    text,
    centerX,
    centerY,
    width,
    height,
    left: centerX - width / 2,
    top: centerY - height / 2,
    right: centerX + width / 2,
    bottom: centerY + height / 2,
  };
}

function drawTshirt(
  ctx: CanvasRenderingContext2D,
  bounds: { w: number; h: number },
  side: TshirtSide,
  data: SideRenderData,
  loadedImage: HTMLImageElement | null,
  loadedTemplate: HTMLImageElement | null,
  templateBounds: TemplateContentBounds | null,
  shirtColor: string,
) {
  const { w, h } = bounds;
  const layout =
    loadedTemplate && templateBounds
      ? getTemplateLayout(w, h, side, templateBounds)
      : getLayout(w, h, side);

  ctx.clearRect(0, 0, w, h);
  ctx.fillStyle = "#ffffff";
  ctx.fillRect(0, 0, w, h);

  if (loadedTemplate) {
    ctx.drawImage(
      loadedTemplate,
      layout.shirtX,
      layout.shirtY,
      layout.shirtW,
      layout.shirtH,
    );

    const designCanvas = document.createElement("canvas");
    designCanvas.width = Math.max(1, Math.round(w));
    designCanvas.height = Math.max(1, Math.round(h));
    const dctx = designCanvas.getContext("2d");
    if (dctx) {
      if (loadedImage) {
        const baseScale = Math.max(
          layout.printW / loadedImage.width,
          layout.printH / loadedImage.height,
        );
        const drawW = loadedImage.width * baseScale;
        const drawH = loadedImage.height * baseScale;
        const shiftX = (data.offsetXPct / 100) * layout.printW * 0.9;
        const shiftY = (data.offsetYPct / 100) * layout.printH * 0.9;
        dctx.save();
        dctx.beginPath();
        dctx.rect(layout.printX, layout.printY, layout.printW, layout.printH);
        dctx.clip();
        dctx.translate(
          layout.printX + layout.printW / 2 + shiftX,
          layout.printY + layout.printH / 2 + shiftY,
        );
        dctx.rotate((data.rotation * Math.PI) / 180);
        dctx.scale(data.scale, data.scale);
        dctx.drawImage(loadedImage, -drawW / 2, -drawH / 2, drawW, drawH);
        dctx.restore();
      }

      const textMetrics = computeTextMetrics(dctx, layout, data);
      if (textMetrics) {
        const textBounds = getTextBounds(layout);
        const textSize = getRenderedTextSizePx(layout, data);
        const weight = clamp(Number(data.textWeight ?? 400), 100, 700);
        const italic = data.textItalic ? "italic " : "";
        const family = data.textFont || "Impact";
        const letterSpacing = getRenderedLetterSpacingPx(layout, data);
        dctx.save();
        dctx.beginPath();
        dctx.rect(textBounds.x, textBounds.y, textBounds.w, textBounds.h);
        dctx.clip();
        dctx.fillStyle = data.textColor || "#111827";
        dctx.font = `${italic}${weight} ${Math.round(textSize)}px "${family}"`;
        dctx.textAlign = "center";
        dctx.textBaseline = "middle";
        if (data.textStroke) {
          dctx.strokeStyle = "#ffffff";
          dctx.lineWidth = Math.max(1, textSize * 0.09);
          dctx.strokeText(
            textMetrics.text,
            textMetrics.centerX,
            textMetrics.centerY,
          );
        }
        if (Math.abs(letterSpacing) < 0.1) {
          dctx.fillText(
            textMetrics.text,
            textMetrics.centerX,
            textMetrics.centerY,
          );
        } else {
          let x = textMetrics.left;
          for (const ch of textMetrics.text) {
            const m = dctx.measureText(ch);
            const charW = m.width;
            dctx.fillText(ch, x + charW / 2, textMetrics.centerY);
            x += charW + letterSpacing;
          }
        }
        dctx.restore();
      }

      dctx.save();
      dctx.globalCompositeOperation = "destination-in";
      dctx.drawImage(
        loadedTemplate,
        layout.shirtX,
        layout.shirtY,
        layout.shirtW,
        layout.shirtH,
      );
      dctx.restore();
      ctx.drawImage(designCanvas, 0, 0, w, h);
    }
    return;
  }

  drawShirtPath(ctx, layout);
  ctx.fillStyle = shirtColor || "#ffffff";
  ctx.fill();

  ctx.save();
  ctx.globalCompositeOperation = "destination-out";
  drawNeckCutPath(ctx, layout);
  ctx.fill();
  ctx.restore();

  if (loadedImage) {
    ctx.save();
    drawShirtPath(ctx, layout);
    ctx.clip();
    ctx.beginPath();
    ctx.rect(layout.printX, layout.printY, layout.printW, layout.printH);
    ctx.clip();
    const baseScale = Math.max(
      layout.printW / loadedImage.width,
      layout.printH / loadedImage.height,
    );
    const drawW = loadedImage.width * baseScale;
    const drawH = loadedImage.height * baseScale;
    const shiftX = (data.offsetXPct / 100) * layout.printW * 0.9;
    const shiftY = (data.offsetYPct / 100) * layout.printH * 0.9;
    ctx.translate(
      layout.printX + layout.printW / 2 + shiftX,
      layout.printY + layout.printH / 2 + shiftY,
    );
    ctx.rotate((data.rotation * Math.PI) / 180);
    ctx.scale(data.scale, data.scale);
    ctx.drawImage(loadedImage, -drawW / 2, -drawH / 2, drawW, drawH);
    ctx.restore();
  }

  const textMetrics = computeTextMetrics(ctx, layout, data);
  if (textMetrics) {
    const textBounds = getTextBounds(layout);
    const textSize = getRenderedTextSizePx(layout, data);
    const weight = clamp(Number(data.textWeight ?? 400), 100, 700);
    const italic = data.textItalic ? "italic " : "";
    const family = data.textFont || "Impact";
    const letterSpacing = getRenderedLetterSpacingPx(layout, data);
    ctx.save();
    drawShirtPath(ctx, layout);
    ctx.clip();
    ctx.beginPath();
    ctx.rect(textBounds.x, textBounds.y, textBounds.w, textBounds.h);
    ctx.clip();
    ctx.fillStyle = data.textColor || "#111827";
    ctx.font = `${italic}${weight} ${Math.round(textSize)}px "${family}"`;
    ctx.textAlign = "center";
    ctx.textBaseline = "middle";
    if (data.textStroke) {
      ctx.strokeStyle = "#ffffff";
      ctx.lineWidth = Math.max(1, textSize * 0.09);
      ctx.strokeText(
        textMetrics.text,
        textMetrics.centerX,
        textMetrics.centerY,
      );
    }
    if (Math.abs(letterSpacing) < 0.1) {
      ctx.fillText(textMetrics.text, textMetrics.centerX, textMetrics.centerY);
    } else {
      let x = textMetrics.left;
      for (const ch of textMetrics.text) {
        const m = ctx.measureText(ch);
        const charW = m.width;
        ctx.fillText(ch, x + charW / 2, textMetrics.centerY);
        x += charW + letterSpacing;
      }
    }
    ctx.restore();
  }

  drawShirtPath(ctx, layout);
  ctx.strokeStyle = "#94a3b8";
  ctx.lineWidth = Math.max(1, layout.shirtW * 0.0085);
  ctx.stroke();
  if (side === "front" && layout.neckDepth > 0) {
    drawNeckCutPath(ctx, layout);
    ctx.strokeStyle = "#cbd5e1";
    ctx.lineWidth = Math.max(1, layout.shirtW * 0.006);
    ctx.stroke();
  }
  if (side === "back") {
    drawBackNeckSeamPath(ctx, layout);
    ctx.strokeStyle = "#cbd5e1";
    ctx.lineWidth = Math.max(1, layout.shirtW * 0.0055);
    ctx.stroke();
  }
}

function normalizeSide(raw: any): SideRenderData {
  const image =
    raw?.image && typeof raw.image === "object" ? (raw.image as any) : {};
  const textRaw = raw?.text;
  const text =
    textRaw && typeof textRaw === "object" ? (textRaw as any) : ({} as any);
  const textStyle =
    raw?.textStyle && typeof raw.textStyle === "object"
      ? (raw.textStyle as any)
      : ({} as any);
  const textValue =
    typeof textRaw === "string"
      ? textRaw
      : pickFirst(text.value, raw?.textValue, "");
  return {
    imageUrl: resolveAsset(
      toText(
        pickFirst(
          image.imageUrl,
          raw?.imageUrl,
          image.sourceImageUrl,
          raw?.sourceImageUrl,
          "",
        ),
      ),
    ),
    scale: toNum(pickFirst(image.scale, raw?.scale, 1), 1),
    rotation: toNum(pickFirst(image.rotation, raw?.rotation, 0), 0),
    offsetXPct: clamp(
      toNum(pickFirst(image.offsetXPct, raw?.offsetXPct, 0), 0),
      -90,
      90,
    ),
    offsetYPct: clamp(
      toNum(pickFirst(image.offsetYPct, raw?.offsetYPct, 0), 0),
      -90,
      90,
    ),
    text: toText(textValue),
    textFont: toText(
      pickFirst(text.font, textStyle.font, raw?.textFont, "Impact"),
    ),
    textSizePx: clamp(
      toNum(pickFirst(text.sizePx, textStyle.sizePx, raw?.textSizePx, 24), 24),
      12,
      32,
    ),
    textOffsetXPct: clamp(
      toNum(pickFirst(text.xPct, textStyle.xPct, raw?.textOffsetXPct, 0), 0),
      -100,
      100,
    ),
    textOffsetYPct: clamp(
      toNum(pickFirst(text.yPct, textStyle.yPct, raw?.textOffsetYPct, 4), 4),
      -100,
      100,
    ),
    textColor: toText(
      pickFirst(text.color, textStyle.color, raw?.textColor, "#111827"),
    ),
    textWeight: clamp(
      toNum(
        pickFirst(text.weight, textStyle.weight, raw?.textWeight, 400),
        400,
      ),
      100,
      700,
    ),
    textItalic: Boolean(
      pickFirst(text.italic, textStyle.italic, raw?.textItalic, false),
    ),
    textStroke: Boolean(
      pickFirst(text.stroke, textStyle.stroke, raw?.textStroke, false),
    ),
    textUppercase: Boolean(
      pickFirst(text.uppercase, textStyle.uppercase, raw?.textUppercase, true),
    ),
    textLetterSpacingPx: toNum(
      pickFirst(
        text.letterSpacingPx,
        textStyle.letterSpacingPx,
        raw?.textLetterSpacingPx,
        4,
      ),
      4,
    ),
  };
}

function TshirtSideCanvas({
  side,
  sideConfig,
  templateImage,
  shirtColor,
}: {
  side: TshirtSide;
  sideConfig: any;
  templateImage?: string;
  shirtColor: string;
}) {
  const data = useMemo(() => normalizeSide(sideConfig), [sideConfig]);
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const [loadedImage, setLoadedImage] = useState<HTMLImageElement | null>(null);
  const [loadedTemplate, setLoadedTemplate] = useState<HTMLImageElement | null>(
    null,
  );
  const [templateBounds, setTemplateBounds] =
    useState<TemplateContentBounds | null>(null);
  const [canvasSize, setCanvasSize] = useState({ width: 0, height: 0 });

  useEffect(() => {
    if (!data.imageUrl) {
      setLoadedImage(null);
      return;
    }
    let cancelled = false;
    const img = new Image();
    img.onload = () => {
      if (!cancelled) setLoadedImage(img);
    };
    img.onerror = () => {
      if (!cancelled) setLoadedImage(null);
    };
    img.src = data.imageUrl;
    return () => {
      cancelled = true;
    };
  }, [data.imageUrl]);

  const resolvedTemplateImage = resolveAsset(templateImage);
  useEffect(() => {
    if (!resolvedTemplateImage) {
      setLoadedTemplate(null);
      setTemplateBounds(null);
      return;
    }
    let cancelled = false;
    const loadTemplate = (corsFirst: boolean) => {
      const img = new Image();
      if (corsFirst) img.crossOrigin = "anonymous";
      img.onload = () => {
        if (cancelled) return;
        setTemplateBounds(detectTemplateContentBounds(img));
        setLoadedTemplate(img);
      };
      img.onerror = () => {
        if (cancelled) return;
        if (corsFirst) {
          loadTemplate(false);
          return;
        }
        setTemplateBounds(null);
        setLoadedTemplate(null);
      };
      img.src = resolvedTemplateImage;
    };
    loadTemplate(true);
    return () => {
      cancelled = true;
    };
  }, [resolvedTemplateImage]);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const host = canvas.parentElement;
    if (!host) return;

    const syncSize = () => {
      const bounds = host.getBoundingClientRect();
      const nextWidth = Math.max(0, Math.round(bounds.width));
      const nextHeight = Math.max(0, Math.round(bounds.height));
      setCanvasSize((prev) =>
        prev.width === nextWidth && prev.height === nextHeight
          ? prev
          : { width: nextWidth, height: nextHeight },
      );
    };

    syncSize();
    const ro = new ResizeObserver(syncSize);
    ro.observe(host);
    return () => ro.disconnect();
  }, []);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    if (!canvasSize.width || !canvasSize.height) return;
    const ctx = ensureCanvasSize(canvas, canvasSize.width, canvasSize.height);
    if (!ctx) return;
    drawTshirt(
      ctx,
      { w: canvasSize.width, h: canvasSize.height },
      side,
      data,
      loadedImage,
      loadedTemplate,
      templateBounds,
      shirtColor,
    );
  }, [
    canvasSize.height,
    canvasSize.width,
    data,
    loadedImage,
    loadedTemplate,
    shirtColor,
    side,
    templateBounds,
  ]);

  return (
    <canvas
      ref={canvasRef}
      style={{ width: "100%", height: "100%", display: "block" }}
    />
  );
}

export default function TshirtCustomizationPreview({
  tshirtCustomization,
  fallbackColor = "#ffffff",
  title = "Exact T-shirt Customization Preview",
  className,
}: Props) {
  if (!tshirtCustomization) return null;
  const shirtColor = String(
    tshirtCustomization?.color || fallbackColor || "#ffffff",
  );
  const templateFront =
    tshirtCustomization?.template?.frontImage ||
    tshirtCustomization?.preview?.templateFrontImage ||
    "";
  const templateBack =
    tshirtCustomization?.template?.backImage ||
    tshirtCustomization?.preview?.templateBackImage ||
    "";

  const hasFrontDesign =
    !!tshirtCustomization?.front?.image?.imageUrl ||
    !!String(tshirtCustomization?.front?.text?.value || "").trim();
  const hasBackDesign =
    !!tshirtCustomization?.back?.image?.imageUrl ||
    !!String(tshirtCustomization?.back?.text?.value || "").trim();

  if (!hasFrontDesign && !hasBackDesign) return null;

  return (
    <div
      className={className}
      style={{
        marginTop: 8,
        border: "1px solid #d1d5db",
        background: "#fff",
        borderRadius: 8,
        padding: 8,
        width: "100%",
        maxWidth: 320,
        boxSizing: "border-box",
      }}
    >
      <div style={{ fontSize: 12, color: "#374151", marginBottom: 6 }}>
        {title}
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
        {(["front", "back"] as const).map((side) => (
          <div key={side} style={{ display: "grid", gap: 4 }}>
            <div
              style={{
                fontSize: 11,
                color: "#6b7280",
                textTransform: "capitalize",
                fontWeight: 700,
              }}
            >
              {side}
            </div>
            <div
              style={{
                position: "relative",
                width: "100%",
                aspectRatio:
                  side === "front"
                    ? templateFront
                      ? "0.78 / 1"
                      : "1 / 1.2"
                    : templateBack
                      ? "0.78 / 1"
                      : "1 / 1.2",
                border: "1px solid #9ca3af",
                borderRadius: templateFront || templateBack ? 10 : 0,
                overflow: "hidden",
                background: "#ffffff",
              }}
            >
              <TshirtSideCanvas
                side={side}
                sideConfig={tshirtCustomization?.[side] || {}}
                templateImage={side === "front" ? templateFront : templateBack}
                shirtColor={shirtColor}
              />
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
